import React, { useState, useEffect, useMemo } from 'react';
import ProtectedRoute from '../../routes/protected';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Toast } from 'primereact/toast';
import surveyService from '../../services/data/survey.service';
import SurveyPageHeader from './SurveyPageHeader';
import { isRichTextEmpty } from '../../helpers/richText';

/** Nota inicial da escala 0–10 (cursor no meio até o colaborador ajustar). */
const DEFAULT_SURVEY_SCORE = 5;

const SLIDER_STEP_VALUES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/** UUID v4 (aceita qualquer versão no formato padrão) retornado pela API em template.questions[].id */
const QUESTION_UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function getQuestionId(q) {
    if (!q || typeof q !== 'object') return null;
    const raw = q.id ?? q.question_id ?? q.pk;
    if (raw === null || raw === '') return null;
    const s = String(raw).trim();
    return QUESTION_UUID_RE.test(s) ? s : null;
}

export default function SurveyTake() {
    const toast = React.useRef(null);
    const companyId = localStorage.getItem('companyId');
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSurvey, setSelectedSurvey] = useState(null);
    const [answers, setAnswers] = useState({});
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    const sortedQuestions = useMemo(() => {
        if (!selectedSurvey?.template?.questions?.length) return [];
        return [...selectedSurvey.template.questions]
            .filter((q) => getQuestionId(q))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }, [selectedSurvey]);

    useEffect(() => {
        if (!companyId) return;
        surveyService.getPendingSurveys({ company_id: companyId })
            .then(r => setSurveys(r || []))
            .catch(() => setSurveys([]))
            .finally(() => setLoading(false));
    }, [companyId]);

    function handleSelectSurvey(survey) {
        const qs = survey.template?.questions || [];
        const invalid = qs.some((q) => !getQuestionId(q));
        if (invalid) {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Pesquisa indisponível',
                    detail:
                        'As perguntas desta pesquisa não possuem id (UUID) válido. Recarregue a página ou contate o suporte.',
                });
            }
            return;
        }
        setSelectedSurvey(survey);
        const initial = {};
        qs.forEach((q) => {
            const qid = getQuestionId(q);
            if (qid) {
                initial[qid] = {
                    score: q.question_type === 'LIKERT' ? null : DEFAULT_SURVEY_SCORE,
                    comment: '',
                };
            }
        });
        setAnswers(initial);
        setCurrentQuestionIndex(0);
    }

    function handleLeaveSurvey() {
        setSelectedSurvey(null);
        setAnswers({});
        setCurrentQuestionIndex(0);
    }

    function commentRequiredForQuestion(q) {
        const qid = getQuestionId(q);
        if (!qid) return false;
        const a = answers[qid];
        const threshold = q.question_type === 'LIKERT' ? 2 : 6;
        return q.comment_required_if_low && a?.score !== null && a.score <= threshold;
    }

    function goNextQuestion() {
        const q = sortedQuestions[currentQuestionIndex];
        const qid = q ? getQuestionId(q) : null;
        if (q && qid && answers[qid]?.score === null) {
            if (toast.current) {
                toast.current.show({
                    severity: 'warn',
                    summary: 'Atenção',
                    detail: q.question_type === 'LIKERT'
                        ? 'Selecione uma opção antes de avançar.'
                        : 'Selecione uma nota de 0 a 10 antes de avançar.',
                });
            }
            return;
        }
        if (q && qid && commentRequiredForQuestion(q) && !(answers[qid]?.comment || '').trim()) {
            if (toast.current) {
                toast.current.show({
                    severity: 'warn',
                    summary: 'Atenção',
                    detail: 'Esta pergunta exige comentário quando a nota é 6 ou menos.',
                });
            }
            return;
        }
        if (currentQuestionIndex < sortedQuestions.length - 1) {
            setCurrentQuestionIndex((i) => i + 1);
        }
    }

    function goPrevQuestion() {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex((i) => i - 1);
        }
    }

    function handleAnswerChange(questionId, score, comment = '') {
        setAnswers(prev => ({
            ...prev,
            [questionId]: { score, comment },
        }));
    }

    function handleSubmit(e) {
        e.preventDefault();
        if (!selectedSurvey) return;
        for (let i = 0; i < sortedQuestions.length; i++) {
            const q = sortedQuestions[i];
            const qid = getQuestionId(q);
            if (qid && commentRequiredForQuestion(q) && !(answers[qid]?.comment || '').trim()) {
                setCurrentQuestionIndex(i);
                if (toast.current) {
                    toast.current.show({
                        severity: 'warn',
                        summary: 'Atenção',
                        detail: 'Preencha o comentário obrigatório nas perguntas com nota ≤ 6.',
                    });
                }
                return;
            }
        }
        // API survey/submit: campo `question` = apenas UUID (template.questions[].id), nunca o texto da pergunta.
        const ansList = sortedQuestions
            .map((q) => {
                const qid = getQuestionId(q);
                if (!qid) return null;
                const v = answers[qid];
                if (!v || v.score === null) return null;
                return { question: qid, score: v.score, comment: (v.comment || '').trim() };
            })
            .filter(Boolean);
        if (ansList.length === 0) {
            if (toast.current) toast.current.show({ severity: 'warn', summary: 'Atenção', detail: 'Responda pelo menos uma pergunta' });
            return;
        }
        setSubmitting(true);
        surveyService.submitResponse({
            company_id: companyId,
            survey_id: selectedSurvey.id,
            answers: ansList,
        })
            .then(() => {
                if (toast.current) toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Resposta enviada com sucesso!' });
                handleLeaveSurvey();
                surveyService.getPendingSurveys({ company_id: companyId })
                    .then(r => setSurveys(r || []));
            })
            .catch(err => {
                if (toast.current) toast.current.show({ severity: 'error', summary: 'Erro', detail: err.response?.data?.detail || 'Falha ao enviar' });
            })
            .finally(() => setSubmitting(false));
    }

    const headerTitle = selectedSurvey ? `Responder: ${selectedSurvey.name}` : 'Pesquisas Pendentes';

    return (
        <ProtectedRoute>
            <Toast ref={toast} position="top-right" />
            <div className="container-fluid" style={{ paddingBottom: '200px' }}>
                <SurveyPageHeader title={headerTitle} />
                <div
                    className="my-1 py-3 px-3 rounded-top d-flex justify-content-center"
                    style={{ minHeight: '500px', paddingBottom: '200px', marginBottom: '200px' }}
                >
                    <div className="w-100" style={{ maxWidth: '960px' }}>
                    {loading ? (
                        <div className="text-center py-5 text-muted">Carregando...</div>
                    ) : !selectedSurvey ? (
                        surveys.length === 0 ? (
                            <div className="bg-white rounded border-clean p-5 text-center text-muted mx-auto">
                                <FontAwesomeIcon icon={solid('check-circle')} className="fa-3x mb-3 text-success" />
                                <p>Nenhuma pesquisa pendente no momento.</p>
                            </div>
                        ) : (
                            <div className="row g-3 justify-content-center">
                                {surveys.map(s => (
                                    <div key={s.id} className="col-md-8">
                                        <div className="card border-clean h-100">
                                            <div className="card-body">
                                                <h5 className="card-title">{s.name}</h5>
                                                {!isRichTextEmpty(s.description) ? (
                                                    <div
                                                        className="card-text mb-3"
                                                        dangerouslySetInnerHTML={{ __html: s.description }}
                                                    />
                                                ) : (
                                                    <p className="card-text mb-3">Sem descrição.</p>
                                                )}
                                                <button className="btn btn-success btn-rounded px-4" onClick={() => handleSelectSurvey(s)}>
                                                    Iniciar Respostas
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    ) : (
                        <>
                        <div className="bg-white rounded border-clean p-4 pb-0 mb-2 mx-auto">
                            <h5 className="mb-0">{selectedSurvey.name}</h5>
                            <p className="text-muted mb-3">Todas as respostas são confidenciais.</p>

                            {sortedQuestions.length === 0 ? (
                                <div className="alert alert-warning">Este template não possui perguntas.</div>
                            ) : (
                                <>
                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between align-items-center small text-muted mb-2">
                                            <span className="fw-semibold text-body">
                                                Pergunta {currentQuestionIndex + 1} de {sortedQuestions.length}
                                            </span>
                                            <span>{Math.round(((currentQuestionIndex + 1) / sortedQuestions.length) * 100)}%</span>
                                        </div>
                                        <div className="progress" style={{ height: '10px' }}>
                                            <div
                                                className="progress-bar bg-progress-green"
                                                role="progressbar"
                                                style={{
                                                    width: `${((currentQuestionIndex + 1) / sortedQuestions.length) * 100}%`,
                                                }}
                                                aria-valuenow={currentQuestionIndex + 1}
                                                aria-valuemin={1}
                                                aria-valuemax={sortedQuestions.length}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        <p className="fw-bold mt-3 mb-0 ms-1">Perguntas</p>
                        <div className="bg-white rounded border-clean p-4 mx-auto">
                            <form onSubmit={handleSubmit}>
                                {(() => {
                                    const q = sortedQuestions[currentQuestionIndex];
                                    const idx = currentQuestionIndex;
                                    const qid = q ? getQuestionId(q) : null;
                                    if (!q || !qid) return null;
                                    return (
                                        <div className="mb-4 pb-2">
                                            <label className="form-label fw-semibold fs-6">
                                                {idx + 1}. {q.text}
                                            </label>
                                            {q.question_type === 'LIKERT' ? (
                                                <div className="mb-3">
                                                    <div className="d-flex flex-column gap-2 mt-2">
                                                        {['1','2','3','4','5'].map((val) => {
                                                            const numVal = parseInt(val, 10);
                                                            const optLabel = q.resolved_likert_options?.[val] || val;
                                                            const selected = answers[qid]?.score === numVal;
                                                            return (
                                                                <button
                                                                    key={val}
                                                                    type="button"
                                                                    onClick={() => handleAnswerChange(qid, numVal, answers[qid]?.comment || '')}
                                                                    className={`btn text-start border rounded px-3 py-2 ${selected ? 'btn-success text-white fw-semibold' : 'btn-outline-secondary'}`}
                                                                    style={{ transition: 'all 0.15s' }}
                                                                >
                                                                    <span className={`me-2 fw-bold ${selected ? 'text-white' : 'text-muted'}`}>{val}</span>
                                                                    {optLabel}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="mb-3">
                                                    <div className="d-flex justify-content-between align-items-center gap-2 small text-muted px-1 mb-2">
                                                        <span className="fs-3 fw-bold text-green text-center flex-grow-1">
                                                            {answers[qid]?.score ?? DEFAULT_SURVEY_SCORE}
                                                        </span>
                                                    </div>
                                                    <input
                                                        type="range"
                                                        className="form-range w-100 survey-slider-steps"
                                                        min="0"
                                                        max="10"
                                                        step={1}
                                                        value={answers[qid]?.score ?? DEFAULT_SURVEY_SCORE}
                                                        onChange={(e) =>
                                                            handleAnswerChange(
                                                                qid,
                                                                parseInt(e.target.value, 10),
                                                                answers[qid]?.comment || ''
                                                            )
                                                        }
                                                    />
                                                    <div
                                                        className="d-flex justify-content-between align-items-start px-1 mt-1 mb-2 small user-select-none"
                                                        style={{ fontVariantNumeric: 'tabular-nums' }}
                                                        aria-hidden
                                                    >
                                                        {SLIDER_STEP_VALUES.map((n) => {
                                                            const current = answers[qid]?.score ?? DEFAULT_SURVEY_SCORE;
                                                            const active = n === current;
                                                            return (
                                                                <span
                                                                    key={n}
                                                                    className={`text-center flex-grow-1 ${active ? 'fw-bold text-success' : 'text-muted'}`}
                                                                    style={{ minWidth: 0, lineHeight: 1.1 }}
                                                                >
                                                                    {n}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                    <div className="d-flex justify-content-between">
                                                        <span className="text-start flex-shrink-0" style={{ maxWidth: '30%' }}>{q.anchor_low}</span>
                                                        <span className="text-end flex-shrink-0" style={{ maxWidth: '30%' }}>{q.anchor_high}</span>
                                                    </div>
                                                </div>
                                            )}
                                            {(() => {
                                                const threshold = q.question_type === 'LIKERT' ? 2 : 6;
                                                const score = answers[qid]?.score;
                                                const showComment = q.comment_optional || (q.comment_required_if_low && score !== null && score <= threshold);
                                                if (!showComment) return null;
                                                const isRequired = q.comment_required_if_low && score !== null && score <= threshold;
                                                return (
                                                    <textarea
                                                        className="form-control form-control-sm mt-2"
                                                        placeholder={isRequired ? `Comentário obrigatório (nota ≤ ${threshold})` : 'Comentário (opcional)'}
                                                        rows={3}
                                                        value={answers[qid]?.comment ?? ''}
                                                        onChange={(e) => handleAnswerChange(qid, answers[qid]?.score, e.target.value)}
                                                    />
                                                );
                                            })()}
                                        </div>
                                    );
                                })()}

                                <div className="d-flex justify-content-center flex-wrap gap-2 pt-3 mt-2 border-top">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-rounded px-4"
                                        disabled={currentQuestionIndex === 0}
                                        onClick={goPrevQuestion}
                                    >
                                        <FontAwesomeIcon icon={solid('arrow-left')} className="me-1" />
                                        Anterior
                                    </button>
                                    {currentQuestionIndex < sortedQuestions.length - 1 ? (
                                        <button type="button" className="btn btn-success btn-rounded px-4" onClick={goNextQuestion}>
                                            Próxima
                                            <FontAwesomeIcon icon={solid('arrow-right')} className="ms-1" />
                                        </button>
                                    ) : (
                                        <button type="submit" className="btn btn-success btn-rounded px-4" disabled={submitting}>
                                            {submitting ? 'Enviando...' : 'Enviar resposta'}
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>
                        </>
                    )}
                    </div>
                </div>
            </div>
        </ProtectedRoute>
    );
}
